import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import LocalizedInput from "@/components/LocalizedInput";
import { ProductInitialValues } from "@/data/WebDataManagement/MasterData/Products/initial-values";
import ProductSchema from "@/schema/WebDataManagement/MasterData/Products/ProductSchema";
import RenderToast from "@/components/RenderToast";
import {
  productClassesMasterDataApi,
  productsMasterDataApi,
  sportsMasterDataApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import StatusInput from "@/components/StatusInput";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";

const ProductModal = forwardRef((_, ref) => {
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${productsMasterDataApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Product ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productsMasterDataApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: productsMasterDataApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ProductInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: ProductSchema(localeCodes),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;

  const productClassListApi = `${productClassesMasterDataApi}${baseQueryParams}`;
  const {
    loading: productClassLoading,
    defaultOptions: productClassDefaultOptions,
    debouncedFetchOptions: productClassFetchOptions,
    getSelectedOption: productClassGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productClassListApi,
    formikValue: formik.values.product_class_id,
    formikInitialValue: formik.initialValues.product_class_id,
    showData: !!modalState?.id && showData?.data?.productClass,
  });

  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData: !!modalState?.id && showData?.data?.sport,
  });

  const handleSubmit = (values) => {
    const payload = {
      code: values.code,
      sport_id: values.sport_id,
      product_class_id: values.product_class_id,
      status: values.status,
      localizations: {},
    };
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        payload.localizations[key] = values.localizations[key];
      }
    });
    modalState.id
      ? saveEdit(JSON.stringify(payload))
      : saveCreate(JSON.stringify(payload));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Product" : "Add Product"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport"
            placeholder="No Sport selected. Type to search..."
            defaultOptions={sportDefaultOptions}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={sportGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "sport_id",
                selected?.value || formik.values.sport_id || null
              );
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Product Class"
            placeholder="No Product Class selected. Type to search..."
            defaultOptions={productClassDefaultOptions}
            loadOptions={productClassFetchOptions}
            isLoading={productClassLoading}
            value={productClassGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "product_class_id",
                selected?.value || formik.values.product_class_id || null
              );
            }}
            errorText={
              (formik.touched.product_class_id &&
                formik.errors.product_class_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.product_class_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />

      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ProductModal;
